import "./rich-text-editor.css";

import Placeholder from "@tiptap/extension-placeholder";
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import { RichTextToolbar } from "./RichTextToolbar";

/**
 * Imperative handle exposed by {@link RichTextEditor}. Mirrors the small slice
 * of the old Quill instance API that call sites relied on (inserting a token at
 * the caret in the notification-template editor).
 */
export interface RichTextEditorHandle {
  /** Insert plain text at the current selection (or at the end if unfocused). */
  insertText: (text: string) => void;
  /**
   * Move DOM focus into the editable surface. A host focus trap (e.g.
   * Mantine's Modal) only reads `data-autofocus` once, before the trap's own
   * boundary node settles - too early for content that mounts later behind a
   * loading or permission gate. Callers in that situation must focus
   * explicitly once the editor actually exists (EWM-1012).
   */
  focus: () => void;
  /** The underlying TipTap editor, for advanced callers. */
  getEditor: () => Editor | null;
}

export interface RichTextEditorProps {
  id?: string;
  className?: string;
  /** Current value as an HTML string (Quill-compatible). */
  value: string | null | undefined;
  placeholder?: string;
  readOnly?: boolean;
  /**
   * Marks the editable surface as the initial-focus target (Mantine's
   * `data-autofocus` convention) so a host focus trap (e.g. a Modal) lands the
   * caret here instead of on an earlier-in-DOM toolbar control such as the
   * heading-level `<select>` (EWM-1012).
   */
  autoFocus?: boolean;
  /** Emits the editor's HTML. Empty content is normalised to `''`. */
  onChange: (value: string) => void;
  onBlur?: () => void;
  onKeyUp?: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

// StarterKit (>=3) bundles Bold, Italic, Underline, Strike, Heading, Lists and
// Link, so a single configured StarterKit covers every format the old Quill
// "snow" toolbar exposed. We only add Placeholder, which is not bundled.
export const buildExtensions = (placeholder?: string) => [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    link: {
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
    },
  }),
  Placeholder.configure({ placeholder: placeholder ?? "" }),
];

/**
 * Does `html` parse to the same ProseMirror document the editor currently holds?
 *
 * The controlled-value sync (below) must distinguish two cases: the incoming
 * `value` is either (a) the editor's own output echoed back by the consumer, or
 * (b) a genuinely external change (async load, RHF reset, language switch).
 * Comparing HTML *strings* is unreliable for (a): consumers such as
 * WysiwygTextField re-run `DOMPurify.sanitize()` on every render, which
 * reorders/normalises attributes and whitespace, so the echoed string no longer
 * byte-matches `editor.getHTML()` even though it represents the identical
 * document. Parsing both into the editor's schema and comparing the resulting
 * documents with `Node.eq()` is serialisation-independent — it answers "is this
 * the same content?" deterministically, regardless of focus (EWM-826).
 *
 * Falls back to byte-equality if the ProseMirror internals aren't reachable
 * (e.g. a mocked editor in unit tests) so the sync degrades safely rather than
 * throwing.
 */
export const htmlMatchesEditorDoc = (editor: Editor, html: string): boolean => {
  try {
    const container = document.createElement("div");
    container.innerHTML = html;
    const incomingDoc = ProseMirrorDOMParser.fromSchema(editor.schema).parse(
      container,
    );
    return incomingDoc.eq(editor.state.doc);
  } catch {
    return html === editor.getHTML();
  }
};

/**
 * TipTap-based rich-text editor. Replacement for the previous Quill editor
 * (CVE-2025-15056). Consumes and emits HTML so already-stored Quill content
 * keeps rendering, and newly authored content stays an HTML string end to end.
 */
export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(
  (
    { id, className, value, placeholder, readOnly, autoFocus, onChange, onBlur, onKeyUp },
    ref,
  ) => {
    // Guards the controlled-value sync below from firing onChange in a loop:
    // when we programmatically setContent we don't want to re-emit it.
    const isSettingContent = useRef(false);

    const editor = useEditor({
      extensions: buildExtensions(placeholder),
      content: value ?? "",
      editable: !readOnly,
      editorProps: {
        attributes: {
          // `wysiwyg-content` carries the shared prose styling; consumers may add
          // layout via `className`.
          class: `wysiwyg-content ${className ?? ""}`.trim(),
          ...(id ? { id } : {}),
          ...(autoFocus ? { "data-autofocus": "true" } : {}),
        },
      },
      onUpdate: ({ editor: instance }) => {
        if (isSettingContent.current) return;
        const html = instance.getHTML();
        // TipTap emits `<p></p>` for an empty doc; normalise to '' so required
        // validators behave exactly as they did with the old stripHtml() guard.
        onChange(instance.getText().trim() === "" ? "" : html);
      },
      onBlur: () => onBlur?.(),
    });

    // Keep the editor in sync when the controlled value changes from outside
    // (e.g. async load, react-hook-form reset) without clobbering the caret on
    // every keystroke: only setContent when the incoming HTML actually differs.
    useEffect(() => {
      // `isDestroyed` guard: a controlled-value change can land in the same commit
      // that tears the editor down (e.g. the estimation-model page resets RHF
      // `values` on async load, re-rendering every bound editor). Calling getHTML()
      // on a destroyed instance dereferences a null schema and throws
      // "Cannot read properties of null (reading 'cached')", killing the page.
      if (!editor || editor.isDestroyed) return;
      const incoming = value ?? "";
      // Primary, deterministic guard (EWM-826): if the incoming HTML represents
      // the same document the editor already holds, this is the editor's own
      // output echoed back — re-sanitised by consumers such as WysiwygTextField,
      // so it no longer byte-matches editor.getHTML() even though the content is
      // identical. Skipping it here (comparing documents, not strings) leaves
      // live typing untouched without depending on focus state.
      if (htmlMatchesEditorDoc(editor, incoming)) return;
      // Defensive net: never reset the caret out from under an actively-typing
      // user, even in the pathological case where a consumer's sanitiser rewrites
      // the document mid-keystroke. External changes (async load, form reset,
      // language switch) always land while the editor is unfocused, so this
      // never blocks a genuine sync.
      if (editor.isFocused) return;
      isSettingContent.current = true;
      editor.commands.setContent(incoming, { emitUpdate: false });
      isSettingContent.current = false;
    }, [editor, value]);

    useEffect(() => {
      if (!editor || editor.isDestroyed) return;
      editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    useImperativeHandle(
      ref,
      () => ({
        insertText: (text: string) => {
          if (!editor || editor.isDestroyed) return;
          editor.chain().focus().insertContent(text).run();
        },
        focus: () => {
          if (!editor || editor.isDestroyed) return;
          // The higher-level `editor.commands.focus()` defers the actual DOM
          // focus() call to a requestAnimationFrame (for Safari/iOS quirks
          // around tap-to-focus). That's invisible in a real browser but
          // means a caller cannot rely on focus having moved synchronously
          // after this returns. The view's own focus() does not defer.
          editor.view.focus();
        },
        getEditor: () => editor,
      }),
      [editor],
    );

    return (
      <div className="wysiwyg-editor">
        {!readOnly && <RichTextToolbar editor={editor} />}
        <EditorContent
          editor={editor}
          onKeyUp={onKeyUp}
          className={readOnly ? "wysiwyg-editor__content--readonly" : undefined}
        />
      </div>
    );
  },
);

RichTextEditor.displayName = "RichTextEditor";
